/*
 * Copyright (c) 2016-present, Parse, LLC
 * All rights reserved.
 *
 * This source code is licensed under the license found in the LICENSE file in
 * the root directory of this source tree.
 */
import * as Filters from 'lib/Filters';
import * as PushUtils from 'lib/PushUtils';
import * as PushConstants from 'dashboard/Push/PushConstants';
import Button from 'components/Button/Button.react';
import Field from 'components/Field/Field.react';
import Filter from 'components/Filter/Filter.react';
import FilterRow from 'components/BrowserFilter/FilterRow.react';
import FormNote from 'components/FormNote/FormNote.react';
// import InstallationCondition  from 'components/PushAudienceDialog/InstallationCondition.react';
import Label from 'components/Label/Label.react';
import Modal from 'components/Modal/Modal.react';
import modalStyles from 'components/Modal/Modal.scss';
import MultiSelectIcons from 'components/MultiSelectIcons/MultiSelectIcons.react';
import MultiSelectIconsOption from 'components/MultiSelectIcons/MultiSelectIconsOption.react';
import prettyNumber from 'lib/prettyNumber';
import ParseApp from 'lib/ParseApp';
import queryFromFilters from 'lib/queryFromFilters';
import React from 'react';
import PropTypes from 'prop-types';
import styles from 'components/PushAudienceDialog/PushAudienceDialog.scss';
import TextInput from 'components/TextInput/TextInput.react';
import Toggle from 'components/Toggle/Toggle.react';
import { List, Map } from 'immutable';

const PARSE_SERVER_SUPPORTS_SAVED_AUDIENCES = true;
const AUDIENCE_SIZE_FETCHING_ENABLED = true;

let filterFormatter = (filters, schema) => {
  return filters.map((filter) => {
    let type = schema[filter.get('field')];
    if (Filters.Constraints[filter.get('constraint')].hasOwnProperty('field')) {
      type = Filters.Constraints[filter.get('constraint')].field;
    }
    // Format any stringified fields
    if (type === 'Number') {
      return filter.set('compareTo', parseFloat(filter.get('compareTo')));
    }

    return filter;
  });
}

let constraintFormatter = (constraint) => {
  return constraint.replace('$', '');
}

export default class PushAudienceDialog extends React.Component {
  constructor() {
    super();
    this.xhrHandle = null;
    this.state = {
      platforms: [],
      filters: new List(),
      saveForFuture: false,
      disabled: true,
      audienceName: '',
      audienceSize: undefined,
      approximate: false,
    };

    this.handleKey = this.handleKey.bind(this);
    this.onCloseClick = this.onCloseClick.bind(this);
  }

  componentWillMount() {
    let stateSettings = {};
    let audienceInfo = this.props.audienceInfo;
    //this case is only for 'New Segment' to prepopulate existing audience
    if (audienceInfo) {
      if (audienceInfo.query) {
        let { deviceType, ...query } = audienceInfo.query;
        stateSettings.platforms = deviceType.$in || [];
      }
      if (audienceInfo.filters) {
        stateSettings.filters = audienceInfo.filters;
      }
      if (audienceInfo.name) {
        stateSettings.audienceName = audienceInfo.name;
      }
      this.setState(stateSettings, this.fetchAudienceSize.bind(this));
    }
  }

  componentDidMount() {
    document.body.addEventListener('keydown', this.handleKey);
  }

  componentWillUnmount() {
    document.body.removeEventListener('keydown', this.handleKey);

    if (this.xhrHandle) {
      this.xhrHandle.abort();
    }
  }

  onCloseClick() {
    this.props.secondaryAction()
  }

  async fetchAudienceSize() {
    if (!this.context || !this.context.currentApp) { //so we don't break the PIG demo
      return;
    }

    let query = {};
    let parseQuery = queryFromFilters('_Installation', this.state.filters);

    if (parseQuery && parseQuery.toJSON()) {
      query = parseQuery.toJSON().where || {};
    }
    query.deviceType = { $in: this.state.platforms };

    try {
      let count = await this.context.currentApp.fetchPushSubscriberCount(PushConstants.NEW_SEGMENT_ID, query);
      this.setState({
        audienceSize: count,
      });

    } catch (e) {
      // console.log('e', e);
      this.setState({
        audienceSize: 0,
      });
    }
  }

  handleAddCondition() {
    let available = Filters.availableFilters(this.props.schema, this.state.filters);
    let field = Object.keys(available)[0];

    this.setState(({ filters }) => ({
      filters: filters.push(new Map({ field: field, constraint: available[field][0] }))
    }), this.fetchAudienceSize.bind(this));
  }

  handleAudienceName(name) {
    //TODO: add some client side regex validation for immediate feedback
    this.setState({ audienceName: name });
  }

  handleChange(newValue) {
    this.setState(
      { platforms: newValue },
      this.fetchAudienceSize.bind(this)
    );
  }

  handleKey(e) {
    var keyCode = e.keyCode || e.which;
    switch (keyCode) {
      case 27:
        this.onCloseClick(this);
        break;
    }
  }

  handleSaveForFuture(value) {
    this.setState({ saveForFuture: value });
  }

  valid() {
    if (this.state.platforms.length === 0) {//check that at least one platform is chosen
      return false;
    }

    if ((this.state.saveForFuture || this.props.disableNewSegment) && this.state.audienceName.length === 0) { //check that a name is written
      return false;
    }
    //TODO check if conditions are valid
    return true;
  }

  render() {
    let options = [];
    // TODO: Parse not return correct devices
    // Quick fix
    // let availableDevices = this.props.availableDevices;
    const availableDevices = PushConstants.AVAILABLE_DEVICES_FIX;
    // TODO: handle empty case when 0 devices - should display link to device creation.
    // TODO: handle misconfigured device link
    for (let index in availableDevices) {
      options.push(
        <MultiSelectIconsOption
          key={`device${index}`}
          value={availableDevices[index]}>
          {PushConstants.DEVICE_MAP[availableDevices[index]]}
        </MultiSelectIconsOption>
      );
    }
    let platformSelect = (
      <div>
        <MultiSelectIcons value={this.state.platforms}
          onChange={this.handleChange.bind(this)}>
          {options}
        </MultiSelectIcons>
      </div>
    );
    let nonEmptyConditions = this.state.filters.size !== 0 ? true : false;
    let audienceSize = PushUtils.formatCountDetails(this.state.audienceSize, this.state.approximate);
    let customFooter = (
      <div className={styles.customFooter}>
        {AUDIENCE_SIZE_FETCHING_ENABLED ? <div
          className={styles.audienceSize}>
          <div className={styles.audienceSizeText}>AUDIENCE SIZE</div>
          <div className={styles.audienceSizeDescription}>{audienceSize}</div>
        </div> : null}
        <Button
          value='Cancel'
          onClick={this.onCloseClick.bind(this)} />
        <Button
          primary={true}
          progress={this.props.progress}
          value={this.props.progress ? 'Creating audience...' : 'Use this audience'}
          color='blue'
          disabled={!this.valid()}
          onClick={this.props.primaryAction.bind(undefined,
            {
              platforms: this.state.platforms,
              name: this.state.audienceName,
              filters: this.state.filters,
              formattedFilters: filterFormatter(this.state.filters, this.props.schema),
              saveForFuture: this.state.saveForFuture,
            })} />
      </div>
    );

    let futureUseSegment = [];

    if (!this.props.disableNewSegment) {
      if (PARSE_SERVER_SUPPORTS_SAVED_AUDIENCES) {
        futureUseSegment.push(
          <Field
            key={'saveForFuture'}
            className='oneliner'
            label={<Label text='Save this audience for future use?' className='oneliner' />}
            fieldPadding={'10 30 ' + (this.state.saveForFuture ? '6' : '10') + ' 30'}
            input={<Toggle value={this.state.saveForFuture} type={Toggle.Types.YES_NO} onChange={this.handleSaveForFuture.bind(this)} />} />
        );
      }

      if (this.state.saveForFuture) {
        futureUseSegment.push(
          <Field
            fieldPadding={'0 30 10 30'}
            key={'audienceName'}
            input={<TextInput placeholder='Choose a Audience Name...' onChange={this.handleAudienceName.bind(this)} />} />
        );
      }
    } else {
      futureUseSegment.push(
        <Field
          label={<Label text='Audience name' />}
          fieldPadding={'10 30 10 30'}
          key={'audienceName'}
          input={<TextInput placeholder='Choose a Audience Name...' onChange={this.handleAudienceName.bind(this)} />} />
      );
    }

    let show_conditions = (
      <div>
        {nonEmptyConditions ?
          <div>
            <Field
              label={<Label text='Conditions' />}
              fieldPadding={'10 30 0 30'} />
            <div className={styles.filter}>
              <Filter
                schema={this.props.schema}
                filters={this.state.filters}
                onChange={(filters) => { this.setState({ filters }, this.fetchAudienceSize.bind(this)) }}
                renderRow={(props) => <FilterRow {...props} />}
              />
            </div>
          </div> : null}
        <Field
          fieldPadding={'10 30 10 30'}
          input={
            Object.keys(this.props.schema).length
              ? <Button value={nonEmptyConditions ? 'Add another condition' : 'Add a condition'} onClick={this.handleAddCondition.bind(this)} />
              : <div>
                <Button disabled={true} value={nonEmptyConditions ? 'Add another condition' : 'Add a condition'} onClick={this.handleAddCondition.bind(this)} />
                <span className={styles.emptyConditions}>Sorry, you do not have any installations yet.</span>
              </div>
          }
        />
      </div>
    );

    return (
      <Modal
        title={this.props.editMode ? 'Edit audience' : 'Create a new audience'}
        type={Modal.Types.INFO}
        icon='plus-outline'
        width={700}
        customFooter={customFooter} >
        <Field
          label={<Label text='Which platforms should be included?' />}
          input={platformSelect} />

        <div className={modalStyles.hr} />

        {show_conditions}

        <div className={modalStyles.hr} />

        {futureUseSegment}
        <FormNote
          show={Boolean(this.props.errorMessage && this.props.errorMessage.length > 0)}
          color='red' >
          {this.props.errorMessage}
        </FormNote>
      </Modal>
    );
  }
}

PushAudienceDialog.contextTypes = {
  currentApp: PropTypes.instanceOf(ParseApp)
};

PushAudienceDialog.propTypes = {
  editMode: PropTypes.bool,
  primaryAction: PropTypes.func.isRequired,
  secondaryAction: PropTypes.func.isRequired,
  schema: PropTypes.object.isRequired,
  audienceInfo: PropTypes.object,
  disableNewSegment: PropTypes.bool,
  availableDevices: PropTypes.arrayOf(PropTypes.string),
};
